import React, { useState } from 'react';

import Button from 'src/components/Button';
import DateComponent from 'src/components/DateComponent';
import Icon from 'src/components/Icon';
import ModalHeader from 'src/components/ModalHeader';
import SideModal from 'src/components/SideModal';
import TextTooltip from 'src/components/TextTooltip';
import { getAuditActivityMetadata } from 'src/utils/auditActivityMetadata';
import { ICONS } from 'src/utils/constants';
import * as variables from 'src/styles/variables';
import styles from './styles.module.scss';

export interface AuditHistoryChange {
    label: string;
    propertyName?: string;
    modelProperty?: string;
    modelPropertyType?: string;
    sequenceNumber?: number;
    jsonPointer?: string;
    oldValue?: any;
    newValue?: any;
    oldValueUnavailable?: boolean;
}

export interface AuditHistoryEvent {
    id: string;
    source: 'org' | 'index';
    eventType: string;
    auditDttm: string;
    count: number;
    actor?: {
        indexUserId?: number | string | null;
        orgUserId?: number | string | null;
        userName?: string | null;
        firstName?: string | null;
        lastName?: string | null;
    };
    entityKeys: { [key: string]: any };
    sourceInfo: { [key: string]: any };
    context?: { [key: string]: any };
    summary?: string | null;
    changes: AuditHistoryChange[];
}

interface AuditHistoryModalProps {
    title: string;
    emptyText: string;
    events: AuditHistoryEvent[];
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    error?: string;
    analytics: {
        loadMore: string;
        toggleChange: string;
    };
    showActor?: boolean;
    showActorBadge?: boolean;
    getEventTitle?: (event: AuditHistoryEvent) => React.ReactNode;
    onClose: Function;
    onLoadMore: Function;
}

function getKeyLabel(key: string) {
    const labels: { [key: string]: string } = {
        encounterFormId: 'Form',
        encounterId: 'Encounter',
        patientId: 'Patient',
        facilityId: 'Facility',
        facId: 'Facility',
        inviteId: 'Invite',
        userId: 'User',
        userPrefId: 'Preference',
        preferenceName: 'Preference',
        app: 'App',
        appVersion: 'Version',
        browser: 'Browser',
        os: 'OS',
        ipAddress: 'IP address',
    };

    return labels[key] || key;
}

function formatSource(sourceInfo: { [key: string]: any }) {
    return [sourceInfo?.app, sourceInfo?.appVersion, sourceInfo?.browser, sourceInfo?.os].filter(Boolean).join(' | ');
}

function getSourceIcon(sourceInfo: { [key: string]: any }): { name: string; type?: 'fa' | 'custom' } {
    const appName = String(sourceInfo?.app || '').toLowerCase();
    return appName.includes('mobile') ? { name: ICONS.MobilePhone2 } : { name: 'window-maximize', type: 'fa' };
}

function getEventLabel(event: AuditHistoryEvent) {
    if (event.eventType === 'ENCTR_FORM_MODEL_UPDATED') {
        return 'Updated form';
    }

    return getAuditActivityMetadata(event.eventType).label;
}

function getDefaultEventTitle(event: AuditHistoryEvent) {
    return event.summary || getEventLabel(event);
}

function getChangedFieldNames(changes: AuditHistoryChange[]): string[] {
    return changes
        .map((change) => change.label || change.modelProperty || change.propertyName)
        .filter((fieldName) => fieldName !== null && fieldName !== undefined)
        .map(String);
}

function getActorName(event: AuditHistoryEvent) {
    const actor = event.actor || {};
    return [actor.firstName, actor.lastName].filter(Boolean).join(' ') || actor.userName || null;
}

function getActorInitials(event: AuditHistoryEvent) {
    const actor = event.actor || {};
    const initials = [actor.firstName, actor.lastName]
        .filter(Boolean)
        .map((name) => name.charAt(0).toUpperCase())
        .join('');

    if (initials) {
        return initials;
    }

    const userName = String(actor.userName || '').trim();
    return userName ? userName.slice(0, 2).toUpperCase() : null;
}

function formatValue(value: any) {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
    }

    return String(value);
}

function AuditHistoryModal(props: AuditHistoryModalProps) {
    const [expandedEvents, setExpandedEvents] = useState<{ [key: string]: boolean }>({});

    function toggleEvent(eventId: string) {
        setExpandedEvents((expanded) => ({ ...expanded, [eventId]: !expanded[eventId] }));
    }

    function renderIdentifier(value: string, key?: string | number) {
        return (
            <span className={styles.identifier} key={key}>
                {value}
            </span>
        );
    }

    function renderChangedFieldList(fieldNames: string[]) {
        const visibleFields = fieldNames.slice(0, 10);
        const otherCount = fieldNames.length - visibleFields.length;

        return (
            <>
                {visibleFields.map((fieldName, index) => (
                    <React.Fragment key={`${fieldName}-${index}`}>
                        {index > 0 ? ', ' : null}
                        {renderIdentifier(fieldName)}
                    </React.Fragment>
                ))}
                {otherCount > 0 ? `, and ${otherCount} ${otherCount === 1 ? 'other' : 'others'}` : null}
            </>
        );
    }

    function renderEventDescription(event: AuditHistoryEvent) {
        if (event.eventType !== 'ENCTR_FORM_MODEL_UPDATED' || event.changes.length === 0) {
            return null;
        }

        const fieldNames = getChangedFieldNames(event.changes);
        if (fieldNames.length === 0) {
            return null;
        }

        return (
            <span>
                {props.showActor ? getActorName(event) || 'User' : 'User'} updated {renderChangedFieldList(fieldNames)}
            </span>
        );
    }

    function renderChange(event: AuditHistoryEvent, change: AuditHistoryChange, index: number) {
        const changeKey = `${event.id}-${index}`;
        const oldValue = formatValue(change.oldValue);
        const newValue = formatValue(change.newValue);
        const label = change.label || change.modelProperty || change.propertyName || 'Form input';
        const rawModelProperty =
            change.modelProperty && change.modelProperty !== label ? (
                <span className={styles.rawIdentifier}>({change.modelProperty})</span>
            ) : null;

        return (
            <div className={styles.changeRow} key={changeKey}>
                Changed {renderIdentifier(label)}
                {rawModelProperty ? <> {rawModelProperty}</> : null}
                {change.oldValueUnavailable ? (
                    <>
                        {' '}
                        to <span className={styles.changeValue}>{newValue}</span>
                    </>
                ) : (
                    <>
                        {' '}
                        from <span className={styles.changeValue}>{oldValue}</span> to{' '}
                        <span className={styles.changeValue}>{newValue}</span>
                    </>
                )}
            </div>
        );
    }

    function renderDetailRow(label: string, value: any) {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        return (
            <div className={styles.detailRow} key={label}>
                <span className={styles.detailLabel}>{label}</span>
                <span className={styles.detailValue}>{formatValue(value)}</span>
            </div>
        );
    }

    function renderDetails(event: AuditHistoryEvent) {
        const source = formatSource(event.sourceInfo);
        const contextRows = [
            event.summary ? renderDetailRow('Summary', event.summary) : null,
            props.showActor ? renderDetailRow('Actor', getActorName(event)) : null,
            renderDetailRow('Event', event.eventType),
            renderDetailRow('Audit source', event.source),
            event.count > 1 ? renderDetailRow('Count', event.count) : null,
            source ? renderDetailRow('Client', source) : null,
            ...Object.keys(event.entityKeys || {}).map((key) =>
                renderDetailRow(getKeyLabel(key), event.entityKeys[key]),
            ),
        ];
        const sourceRows = Object.keys(event.sourceInfo || {}).map((key) =>
            renderDetailRow(getKeyLabel(key), event.sourceInfo[key]),
        );

        return (
            <div className={styles.details}>
                {contextRows.some(Boolean) ? <div className={styles.detailGroup}>{contextRows}</div> : null}
                {event.changes.length ? (
                    <div className={styles.changeList}>
                        {event.changes.map((change, changeIndex) => renderChange(event, change, changeIndex))}
                    </div>
                ) : null}
                {sourceRows.some(Boolean) ? <div className={styles.detailGroup}>{sourceRows}</div> : null}
            </div>
        );
    }

    function renderActorBadge(event: AuditHistoryEvent) {
        const actorName = getActorName(event);
        const initials = getActorInitials(event);

        if (!props.showActorBadge || !actorName || !initials) {
            return null;
        }

        return (
            <TextTooltip text={actorName}>
                <span className={styles.actorBadge}>
                    <span className={styles.actorAvatar}>{initials}</span>
                    <span className={styles.actorName}>{actorName}</span>
                </span>
            </TextTooltip>
        );
    }

    function renderEvent(event: AuditHistoryEvent, index: number) {
        const metadata = getAuditActivityMetadata(event.eventType);
        const source = formatSource(event.sourceInfo);
        const sourceIcon = getSourceIcon(event.sourceInfo);
        const description = renderEventDescription(event);
        const isExpanded = !!expandedEvents[event.id];
        const eventTitle = props.getEventTitle ? props.getEventTitle(event) : getDefaultEventTitle(event);

        return (
            <div className={styles.timelineItem} key={event.id}>
                <div className={styles.markerColumn}>
                    <div className={styles.marker}>
                        <Icon name={metadata.icon} size={15} color={variables.grey} />
                    </div>
                    {index < props.events.length - 1 ? <div className={styles.line} /> : null}
                </div>
                <div>
                    <div className={styles.eventHeader}>
                        <div>
                            <div className={styles.eventTitleLine}>
                                {source ? (
                                    <TextTooltip text={source}>
                                        <Icon
                                            className={styles.sourceIcon}
                                            name={sourceIcon.name}
                                            type={sourceIcon.type}
                                            size={15}
                                            color={variables.grey}
                                        />
                                    </TextTooltip>
                                ) : null}
                                {renderActorBadge(event)}
                                <div className={styles.eventTitle}>
                                    {eventTitle}
                                    {event.count > 1 ? ` (${event.count})` : ''}
                                </div>
                                <button
                                    className={styles.eventToggle}
                                    onClick={() => toggleEvent(event.id)}
                                    data-test-id={props.analytics.toggleChange}
                                    aria-label={isExpanded ? 'Hide details' : 'Show details'}
                                >
                                    {isExpanded ? 'Hide details' : 'Show details'}
                                </button>
                            </div>
                            {description ? <div className={styles.eventDescription}>{description}</div> : null}
                        </div>
                        <div className={styles.eventMeta}>
                            <DateComponent date={event.auditDttm} showDateTime={true} useBrowserTimeZone />
                        </div>
                    </div>
                    {isExpanded ? renderDetails(event) : null}
                </div>
            </div>
        );
    }

    return (
        <SideModal isOpen={true} onClose={props.onClose} isWide>
            <ModalHeader title={props.title} onClose={props.onClose} />
            <div className={styles.content}>
                {props.error ? <div className={styles.errorState}>{props.error}</div> : null}
                {!props.error && props.isLoading ? (
                    <div className={styles.loadingState}>Loading audit history...</div>
                ) : null}
                {!props.error && !props.isLoading && props.events.length === 0 ? (
                    <div className={styles.emptyState}>{props.emptyText}</div>
                ) : null}
                {!props.error && !props.isLoading && props.events.length > 0 ? (
                    <div className={styles.timeline}>{props.events.map(renderEvent)}</div>
                ) : null}
                {!props.error && props.hasMore ? (
                    <div className={styles.loadMore}>
                        <Button
                            type="outline"
                            text="Load More"
                            onClick={() => props.onLoadMore()}
                            isLoading={props.isLoadingMore}
                            data-test-id={props.analytics.loadMore}
                        />
                    </div>
                ) : null}
            </div>
        </SideModal>
    );
}

export default AuditHistoryModal;
